import { TmElement } from '@thiememeulenhoff/lit-sdk';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles, tag } from './header.styles';

@customElement(tag)
export class HeaderComponent extends TmElement {
  static override styles = styles;

  readonly #scrollBufferPx = 25;

  #parentElement?: HTMLElement;

  #closestBody?: HTMLElement;

  #scrollPosition = window.scrollY;

  #resizeObserver?: ResizeObserver;

  @property({ type: String, attribute: 'stream-logo' })
  streamLogo?: string;

  @property({ type: String, attribute: 'stream-title' })
  streamTitle?: string;

  @property({ type: String, attribute: 'logo-link' })
  logoLink?: string;

  @property({ type: Boolean, attribute: 'padding' })
  padding = true;

  @property({ type: String, attribute: 'bypass-block-id' })
  bypassBlockId?: string = 'main-content';

  @property({ type: String, attribute: 'bypass-block-text' })
  bypassBlockText?: string = 'Ga naar hoofdinhoud';

  @property({ type: Boolean, attribute: 'non-collapsable' })
  nonCollapsable = false;

  override render() {
    return html`
      <section class="bypass-block" data-test="bypassBlock">
        ${this.#getBypassBlock()}
      </section>
      <section class="logo">
        <slot name="logo">
          <a-logo-icon data-test="logo"></a-logo-icon>
        </slot>
      </section>
      <section class="start">
        ${this.#getStreamLogo()} ${this.#getStreamTitle()}
        <slot name="start"></slot>
      </section>
      <section class="center"><slot name="center"></slot></section>
      <section class="end"><slot></slot></section>
    `;
  }

  protected override onCreate = () => {
    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this);

    if (!this.nonCollapsable) this.listen('scroll', this.#handleScroll, window);

    this.#parentElement = this.parentElement as HTMLElement;
    this.#closestBody = this.closest('body') as HTMLElement;
  };

  protected override onDestroy = () => {
    this.#resizeObserver?.disconnect();

    this.#parentElement?.style.removeProperty('padding-top');
    this.#closestBody?.style.removeProperty('--tm-header-height');
  };

  #getStreamTitle = () => {
    if (this.streamTitle) {
      return html`<span class="stream-title">${this.streamTitle}</span>`;
    }
    return html``;
  };

  #getStreamLogo = () => {
    if (this.streamLogo) {
      return this.logoLink
        ? html`<a href="${this.logoLink}" data-test="stream-logo"
            >${this.#getLogo()}</a
          >`
        : this.#getLogo();
    }
    return html``;
  };

  #getLogo = () => {
    return html`<img
      class="stream-logo"
      src="${this.streamLogo}"
      alt="Stream logo"
    />`;
  };

  #getBypassBlock = () => {
    if (document.getElementById(this.bypassBlockId || 'main-content')) {
      return html`<a
        href="#${this.bypassBlockId}"
        @keydown=${this.#handleKeydown}
      >
        ${this.bypassBlockText}
      </a>`;
    }
    return html``;
  };

  #handleKeydown = (event: KeyboardEvent) => {
    if (event.key === ' ') {
      event.preventDefault();
      (event.target as HTMLAnchorElement).click();
    }
  };

  #handleScroll = () => {
    const currentScrollPos = window.scrollY;

    if (
      this.#scrollPosition - currentScrollPos > this.#scrollBufferPx ||
      this.#scrollPosition < this.#scrollBufferPx
    ) {
      this.style.top = '0';
    } else if (currentScrollPos - this.#scrollPosition > this.#scrollBufferPx) {
      this.style.top = `-${this.offsetHeight}px`;
    }

    this.#scrollPosition = currentScrollPos;
  };

  #handleResize = () => {
    if (!this.padding) {
      return;
    }

    this.#parentElement?.style.setProperty(
      'padding-top',
      `var(--tm-header-height)`,
    );

    this.#closestBody?.style.setProperty(
      '--tm-header-height',
      `${this.offsetHeight}px`,
    );

    this.#adjustEndElement();
  };

  #adjustEndElement = () => {
    const startElement = this.shadowRoot?.querySelector(
      '.start',
    ) as HTMLElement;
    const endElement = this.shadowRoot?.querySelector('.end') as HTMLElement;

    if (!startElement || !endElement) {
      return;
    }

    const startWidth = startElement.getBoundingClientRect().width;
    const endWidth = endElement.getBoundingClientRect().width;
    const availableRoom = window.innerWidth;

    endElement.style.justifyContent =
      startWidth + endWidth >= availableRoom ? 'flex-start' : 'flex-end';
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'a-header': HeaderComponent;
  }
}
